import React, { useState } from 'react';
import { X, Mail, Send, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { WorkOrder } from '../../types';
import { apiService } from '../../services/api';
import toast from 'react-hot-toast';

interface EmailWorkOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  workOrder: WorkOrder;
}

const EmailWorkOrderModal: React.FC<EmailWorkOrderModalProps> = ({
  isOpen,
  onClose,
  workOrder
}) => {
  const [recipientEmail, setRecipientEmail] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [showEmailSuggestions, setShowEmailSuggestions] = useState(false);

  // Get available email addresses from work order
  const availableEmails = React.useMemo(() => {
    const emails = [];
    if (workOrder.clientEmail) {
      emails.push({ email: workOrder.clientEmail, label: 'Client Email' });
    }
    if (workOrder.clientLocationContactEmail && workOrder.clientLocationContactEmail !== workOrder.clientEmail) {
      emails.push({ email: workOrder.clientLocationContactEmail, label: 'Location Contact Email' });
    }
    return emails;
  }, [workOrder]);

  // Set default email when modal opens
  React.useEffect(() => {
    if (isOpen) {
      const defaultEmail = workOrder.clientLocationContactEmail || workOrder.clientEmail || '';
      setRecipientEmail(defaultEmail);
      setCustomMessage('');
      setEmailError('');
      setShowEmailSuggestions(false);
      
      // Validate the default email if it exists
      if (defaultEmail && !validateEmail(defaultEmail)) {
        setEmailError('Please enter a valid email address');
      }
    }
  }, [isOpen, workOrder]);

  // Validate email format
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle email input change
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    setRecipientEmail(email);
    
    // Only show validation error if user has typed something and it's invalid
    if (email.length > 0 && !validateEmail(email)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
    
    setShowEmailSuggestions(email.length > 0 && availableEmails.length > 1);
  };

  // Handle email suggestion click
  const handleEmailSuggestionClick = (email: string) => {
    setRecipientEmail(email);
    setEmailError('');
    setShowEmailSuggestions(false);
  };

  const handleSendEmail = async () => {
    if (!recipientEmail.trim()) {
      setEmailError('Please enter a recipient email address');
      toast.error('Please enter a recipient email address');
      return;
    }

    if (!validateEmail(recipientEmail)) {
      setEmailError('Please enter a valid email address');
      toast.error('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setEmailError('');
    try {
      const response = await apiService.sendWorkOrderEmail(
        workOrder.id,
        recipientEmail.trim(),
        customMessage.trim() || undefined
      );

      console.log('Email response:', response); // Debug log
      
      // The backend returns { message: "Work order email sent successfully" } on success
      // Check for various possible response formats
      const successMessage = response?.message || response?.data?.message || response?.successMessage;
      
      if (response && successMessage) {
        console.log('Email sent successfully:', successMessage);
        toast.success('Work order email sent successfully!');
        onClose();
      } else if (response) {
        // If we got a response but no clear success message, assume success
        // since we didn't get an error
        console.log('Email likely sent successfully (no clear success message):', response);
        toast.success('Work order email sent successfully!');
        onClose();
      } else {
        console.error('No response received');
        toast.error('Failed to send email. Please try again.');
      }
    } catch (error: any) {
      console.error('Error sending work order email:', error);
      const errorMessage = error.response?.data?.message || 'Failed to send email. Please try again.';
      toast.error(errorMessage);
      setEmailError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleSendEmail();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-accent-color-100 dark:bg-accent-color-900 rounded-lg">
              <Mail className="h-5 w-5 text-accent-color dark:text-accent-color-300" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Email Work Order
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Send work order #{workOrder.workOrderNumber} to client
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-accent-color-50 dark:hover:bg-accent-color-800 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Work Order Info */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
              Work Order Details
            </h3>
            <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
              <div><strong>Number:</strong> {workOrder.workOrderNumber}</div>
              <div><strong>Client:</strong> {workOrder.clientName}</div>
              <div><strong>Location:</strong> {workOrder.clientLocationName || 'N/A'}</div>
              <div><strong>Status:</strong> {workOrder.statusName}</div>
            </div>
          </div>

          {/* Recipient Email */}
          <div className="space-y-2">
            <Label htmlFor="recipientEmail" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Recipient Email <span className="text-red-500">*</span>
            </Label>
            <div className="relative">
              <Input
                id="recipientEmail"
                type="email"
                value={recipientEmail}
                onChange={handleEmailChange}
                onFocus={() => setShowEmailSuggestions(availableEmails.length > 1)}
                onBlur={() => setTimeout(() => setShowEmailSuggestions(false), 200)}
                placeholder="client@example.com"
                className={`w-full ${emailError ? 'border-red-500 focus:border-red-500' : ''}`}
                disabled={isLoading}
              />
              {emailError && (
                <div className="flex items-center space-x-1 mt-1 text-sm text-red-600 dark:text-red-400">
                  <AlertCircle className="h-4 w-4" />
                  <span>{emailError}</span>
                </div>
              )}
              
              {/* Email Suggestions */}
              {showEmailSuggestions && availableEmails.length > 1 && (
                <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg">
                  {availableEmails.map((emailOption, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleEmailSuggestionClick(emailOption.email)}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-600 focus:bg-gray-100 dark:focus:bg-gray-600 focus:outline-none"
                    >
                      <div className="font-medium text-gray-900 dark:text-gray-100">{emailOption.email}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{emailOption.label}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {availableEmails.length > 1 && (
              <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
                <CheckCircle className="h-3 w-3" />
                <span>Multiple email addresses available. Click to select.</span>
              </div>
            )}
          </div>

          {/* Custom Message */}
          <div className="space-y-2">
            <Label htmlFor="customMessage" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Custom Message (Optional)
            </Label>
            <Textarea
              id="customMessage"
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Add a personal message to include with the work order..."
              rows={4}
              className="w-full resize-none"
              disabled={isLoading}
            />
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Press Ctrl+Enter to send
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendEmail}
            disabled={isLoading || !recipientEmail.trim()}
            variant="default"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send Email
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EmailWorkOrderModal;
